import { computed, readonly, Ref, ref } from 'vue';

export interface StepDefinition {
  completed: boolean;
  substeps: Array<Omit<StepDefinition, 'substeps'>>;
}

export interface StepperOptions {
  /**
   * If true, the stepper will prevent skipping steps
   */
  linear?: boolean;
  /**
   * The index of the active step
   */
  activeStep?: number;
  /**
   * The index of the active substep
   */
  activeSubstep?: number;
  /**
   * The stepper representation as an array of steps and substeps
   */
  steps?: Array<StepDefinition>;
  /**
   * A ref to the stepper element
   */
  ref?: Ref<HTMLUListElement | null>;
}

export default function useStepper(
  options: StepperOptions = {
    linear: false,
    activeStep: -1,
    activeSubstep: -1,
    steps: [],
  },
) {
  /**
   * The steps and substeps of the stepper, so we can keep track of their state.
   * Can be populated with the `steps` option, or via the `registerStep` function.
   */
  const steps = ref<Array<StepDefinition>>(options.steps ? options.steps : []);

  const stepCount = computed(() => steps.value.length);

  const activeStepIndex = ref(
    options.activeStep !== null && options.activeStep !== undefined ? options.activeStep : -1,
  );
  const activeSubstepIndex = ref(
    options.activeSubstep !== null && options.activeSubstep !== undefined ? options.activeSubstep : -1,
  );

  const stepElements = computed(() => {
    return Array.from((options.ref?.value?.children || []) as HTMLCollectionOf<HTMLLIElement>);
  });

  const activeStepElement = computed(() => stepElements.value[activeStepIndex.value]);

  /**
   * Returns true if the step at the given index has substeps
   * @param stepIndex The index of the step to check
   */
  function stepHasSubsteps(stepIndex: number): boolean {
    return !!steps.value[stepIndex].substeps.length;
  }

  /**
   * Goes to the next step or substep, while flagging the current one as completed.
   * If the current step has substeps, it will go to the next substep if there are any, or to the next substep.
   * And if the next step has substeps, automatically goes to the first substep
   */
  function next(): void {
    const hasNoActiveStep = activeStepIndex.value === -1;
    const isLastStep = activeStepIndex.value === steps.value.length - 1;

    // If there is no active step, we want to activate the first step
    if (hasNoActiveStep) {
      activeStepIndex.value++;

      // If the step we just navigated to has substeps, we want to activate the first substep
      if (stepHasSubsteps(activeStepIndex.value)) {
        activeSubstepIndex.value++;
      }
    } else if (isLastStep) {
      if (stepHasSubsteps(activeStepIndex.value)) {
        const hasNoActiveSubstep = activeSubstepIndex.value === -1;
        const isLastSubstep = activeSubstepIndex.value === steps.value[activeStepIndex.value].substeps.length - 1;

        // if there are no active substeps, we want to activate the first substep
        if (hasNoActiveSubstep) {
          activeSubstepIndex.value++;
          // if the active substep is the last one, we want to reset the step and substep to -1
        } else if (isLastSubstep) {
          steps.value[activeStepIndex.value].substeps[activeSubstepIndex.value].completed = true;
          activeSubstepIndex.value = -1;

          steps.value[activeStepIndex.value].completed = true;
          activeStepIndex.value = -1;
          // if there are more substeps, we want to complete the current one and proceed to the next substep
        } else {
          steps.value[activeStepIndex.value].substeps[activeSubstepIndex.value].completed = true;
          activeSubstepIndex.value++;
        }
        // If there are no substeps, we want to complete the current step and reset the step to -1
      } else {
        steps.value[activeStepIndex.value].completed = true;
        activeStepIndex.value = -1;
      }
    } else {
      if (stepHasSubsteps(activeStepIndex.value)) {
        const hasNoActiveSubstep = activeSubstepIndex.value === -1;
        const isLastSubstep = activeSubstepIndex.value === steps.value[activeStepIndex.value].substeps.length - 1;

        // if there are no active substeps, we want to activate the first substep
        if (hasNoActiveSubstep) {
          activeSubstepIndex.value++;
          // if the active substep is the last one, we want to go to the next step
        } else if (isLastSubstep) {
          steps.value[activeStepIndex.value].substeps[activeSubstepIndex.value].completed = true;
          activeSubstepIndex.value = -1;

          steps.value[activeStepIndex.value].completed = true;
          activeStepIndex.value++;

          // If the step we just navigated to has substeps, we want to activate the first substep right away
          if (stepHasSubsteps(activeStepIndex.value) && activeSubstepIndex.value === -1) {
            activeSubstepIndex.value++;
          }
          // if there are more substeps, we want to complete the current one and proceed to the next substep
        } else {
          steps.value[activeStepIndex.value].substeps[activeSubstepIndex.value].completed = true;
          activeSubstepIndex.value++;
        }
      } else {
        steps.value[activeStepIndex.value].completed = true;
        activeStepIndex.value++;

        // If the step we just navigated to has substeps, we want to activate the first substep right away
        if (stepHasSubsteps(activeStepIndex.value) && activeSubstepIndex.value === -1) {
          activeSubstepIndex.value++;
        }
      }
    }
  }

  function back(): void {
    // We don't want to go back if there is no active step
    if (activeStepIndex.value === -1) {
      return;
    }

    if (stepHasSubsteps(activeStepIndex.value)) {
      // If the active substep is the first one, we want to go back to the previous step
      if (activeSubstepIndex.value === 0) {
        activeStepIndex.value--;
        activeSubstepIndex.value--;

        // If the step that we just navigated to has substeps, we want to navigate to the last substep
        if (activeStepIndex.value >= 0 && stepHasSubsteps(activeStepIndex.value)) {
          activeSubstepIndex.value = steps.value[activeStepIndex.value].substeps.length - 1;
        }
      } else {
        activeSubstepIndex.value--;
      }
    } else {
      activeStepIndex.value--;

      // If the step that we just navigated to has substeps, we want to navigate to the last substep
      if (activeStepIndex.value >= 0 && stepHasSubsteps(activeStepIndex.value)) {
        activeSubstepIndex.value = steps.value[activeStepIndex.value].substeps.length - 1;
      }
    }
  }

  /**
   * Navigates to a specific step and substep.
   */
  function goTo(stepIndex: number, substepIndex = -1): void {
    // A linear stepper prevents navigation to incomplete steps
    if (options.linear) {
      if (isStepCompleted(stepIndex - 1) || stepIndex === 0) {
        if (stepHasSubsteps(stepIndex)) {
          if (substepIndex <= 0) {
            activeSubstepIndex.value = 0;
          } else if (isStepCompleted(stepIndex, substepIndex - 1)) {
            activeSubstepIndex.value = substepIndex;
          }
        }
        activeStepIndex.value = stepIndex;
      }
    } else {
      activeStepIndex.value = stepIndex;

      if (stepHasSubsteps(stepIndex)) {
        if (substepIndex < 0) {
          activeSubstepIndex.value = 0;
        } else {
          activeSubstepIndex.value = substepIndex;
        }
      }
    }
  }

  /**
   * Returns true if the step is completed.
   * if the substep index is provided, check if the substep is completed.
   */
  function isStepCompleted(stepIndex: number, substepIndex = -1): boolean {
    if (substepIndex >= 0) {
      return steps.value[stepIndex]?.substeps[substepIndex]?.completed;
    } else {
      return steps.value[stepIndex]?.completed;
    }
  }

  /**
   * Returns true if the step is active.
   * if the substep index is provided, check both if the step and the substep are active.
   */
  function isStepActive(stepIndex: number, substepIndex = -1): boolean {
    if (substepIndex >= 0) {
      return activeStepIndex.value === stepIndex && activeSubstepIndex.value === substepIndex;
    } else {
      return activeStepIndex.value === stepIndex;
    }
  }

  /**
   * Registers a new step or substep into the steps array
   */
  function registerStep(nested = false): void {
    if (nested && !steps.value.length) {
      throw new Error('Cannot register a substep without a parent step.');
    }

    if (nested) {
      steps.value[steps.value.length - 1].substeps.push({ completed: false });
    } else {
      steps.value.push({ completed: false, substeps: [] });
    }
  }

  return {
    steps: readonly(steps),
    stepCount,
    activeStepElement,
    activeStepIndex,
    activeSubstepIndex,
    registerStep,
    next,
    back,
    goTo,
    isStepActive,
    isStepCompleted,
  };
}
